import React from 'react';
import { Sidebar } from 'primereact/sidebar';
import { useQuery } from '@tanstack/react-query';
import { useCompanyContext } from '../../../../context/CompanyContext';
import { ProgressSpinner } from 'primereact/progressspinner';
import ErrorMessage from '../../../../components/messages/ErrorMessage';
import ClaimsTable from '../table/ClaimsTable';
import { fetchClaims } from '@/src/app/services/Insurance/Claim';
import { formatDate } from '@/src/utils/formatUtils';
import { ClaimStatus } from '@/src/app/Interfaces/Insurance/Claim';

type ClaimTableDrilldownProps = {
  visible: boolean;
  hideDrilldown: () => void;
  policy: {
    id: string;
    policy: string;
    termStart: Date;
    termEnd: Date;
  };
  fetchActive?: boolean;
  showActiveColumn?: boolean;
  title?: string;
};

const ClaimTableDrilldown = ({
  visible,
  hideDrilldown,
  policy,
  fetchActive,
  showActiveColumn,
  title,
}: ClaimTableDrilldownProps) => {
  const { selectedCompany } = useCompanyContext();

  const { data, isSuccess, isFetching, isError } = useQuery({
    queryKey: ['getPolicyClaims', selectedCompany],
    queryFn: ({ signal }) => {
      return fetchClaims(
        selectedCompany!,
        fetchActive ? ClaimStatus.Active : undefined,
        policy.id,
        signal
      );
    },

    refetchOnWindowFocus: false,
    enabled: !!selectedCompany,
  });

  return (
    <Sidebar
      visible={visible}
      position="right"
      onHide={hideDrilldown}
      className="w-full max-w-1250px"
    >
      {isFetching && (
        <div className="mx-8 h-full flex align-items-center">
          <ProgressSpinner />
        </div>
      )}
      {isError && (
        <div className="h-full flex align-items-center">
          <ErrorMessage
            content={'Failed to obtain data! Please try again later.'}
          />
        </div>
      )}
      {isSuccess && (
        <div className="blackText text-standard printBackground w-full max-w-1500px pdfDivFitContent">
          <div className="mt-3 px-4 flex justify-content-center sm:justify-content-between gap-3 flex-wrap capitalize">
            <div className="text-28px font-bold flex-grow-1 text-center sm:text-left">
              <div className="mx-auto w-fit sm:ml-0 drilldownHeader">
                {title}
              </div>
            </div>
          </div>
          <div className="mt-5 ml-4">
            <div className="flex column-gap-2 flex-wrap">
              <span className="font-bold">Policy:</span>
              <span>{policy.policy}</span>
            </div>
            <div className="flex column-gap-2">
              <span className="font-bold">Effective Date:</span>
              <span>{formatDate(policy.termStart)}</span>
            </div>
            <div className="flex column-gap-2">
              <span className="font-bold">Expiration Date:</span>
              <span>{formatDate(policy.termEnd)}</span>
            </div>
          </div>
          <div className="my-4" />
          <ClaimsTable
            claims={data ?? []}
            showActiveColumn={showActiveColumn}
            disableHideColumns={true}
            filterStatus={'all'}
            disableEdit={true}
          />
        </div>
      )}
    </Sidebar>
  );
};

export default ClaimTableDrilldown;
